import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { CircularProgress, IconButton, Stack } from '@mui/material'
import Grid from '@mui/material/GridLegacy'

import { useParams } from 'react-router-dom'
import { DownloadRounded, Edit } from '@mui/icons-material'
import { DateTime } from 'luxon'

import { roundNumber } from '@trivie/core/src/utils/roundNumber'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { color } from '@trivie/ui-web/theme'
import { Header } from '@trivie/ui-web/components/header'
import { Text } from '@trivie/ui-web/components/text'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { Insight } from '@trivie/ui-web/components/insight'
import { useStores } from '@trivie/core'
import { Divider } from '@trivie/ui-web/components/divider'

import { SurveySchedule } from './survey-schedule'
import { SurveyCard } from './survey-card'
import { PeopleCard } from './people-card'

export const SurveyDetailsScreen: React.FunctionComponent = observer(() => {
  const { id } = useParams() as any
  const [isEditing, setIsEditing] = useState(false)
  const { surveyStore } = useStores()
  const {
    getSurveyDetails,
    getSurveyStats,
    getSurveyQuestionStats,
    getSelectedSurvey,
    selectedSurvey,
    setSelectedSurvey,
    exportResponses,
  } = surveyStore

  const handleSurveyStats = () => {
    if (getSelectedSurvey()?.isLive || getSelectedSurvey()?.isCompleted) {
      getSurveyStats(id)
      getSurveyQuestionStats(id)
    }
  }
  useEffect(() => {
    getSurveyDetails(id).then(() => {
      handleSurveyStats()
    })
    return () => {
      setSelectedSurvey(null)
    }
  }, [])

  const renderDateRange = () => {
    if (selectedSurvey?.start_datetime != null && selectedSurvey?.end_datetime != null) {
      const start = DateTime.fromISO(selectedSurvey.start_datetime).toFormat('MMM d, yyyy')
      const end = DateTime.fromISO(selectedSurvey.end_datetime).toFormat('MMM d, yyyy')
      return `${start} - ${end}`
    }
  }

  const renderSurveyStats = () => {
    return (
      <Stack mb="16px" direction="row" justifyContent="space-between" useFlexGap gap={2}>
        <Panel sx={{ flex: 1 }}>
          <Insight
            title="Assignees"
            value={selectedSurvey?.users?.length || '0'}
            valueFontSize={'24px'}
          />
        </Panel>
        <Panel sx={{ flex: 1 }}>
          <Insight
            title="Response Rate "
            value={`${roundNumber(selectedSurvey?.response_rate ?? 0)}%`}
            valueFontSize={'24px'}
          />
        </Panel>
        <Panel sx={{ flex: 1 }}>
          <Insight
            title="Time Remaining"
            value={selectedSurvey?.end_datetime ? `${selectedSurvey?.timeRemaining} Days` : '0'}
            valueFontSize={'24px'}
          />
        </Panel>
      </Stack>
    )
  }

  const exportSurveyResponses = async () => {
    const { file_url } = (await exportResponses(id)) as any
    window.open(file_url)
  }

  return (
    <>
      {!selectedSurvey ? (
        <div style={LOADER}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <Header
            title={selectedSurvey.title}
            primaryAction={
              selectedSurvey.isLive || selectedSurvey.isCompleted
                ? {
                    startIcon: <DownloadRounded />,
                    onClick: exportSurveyResponses,
                    text: 'Export Responses',
                  }
                : null
            }
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent:
                selectedSurvey?.isLive || selectedSurvey?.isCompleted
                  ? 'space-between'
                  : 'flex-start',
            }}
          >
            {selectedSurvey?.start_datetime ? (
              <Text style={{ opacity: 0.5 }} variant="body1" text={renderDateRange()} />
            ) : (
              selectedSurvey && (
                <Text
                  style={{ opacity: 0.5, marginTop: 6, marginBottom: 6, fontStyle: 'italic' }}
                  variant="body1"
                  text="Unscheduled"
                />
              )
            )}
            <div style={{ flexDirection: 'row', display: 'flex', justifyContent: 'flex-end' }}>
              {selectedSurvey?.isLive || selectedSurvey?.isCompleted ? (
                <GenerateReport filename={`survey-${id}`} />
              ) : null}
            </div>

            {selectedSurvey && !selectedSurvey.isCompleted && !selectedSurvey.isLive ? (
              <IconButton onClick={() => setIsEditing(true)}>
                <Edit htmlColor={color.shade50} style={{ fontSize: 14 }} />
              </IconButton>
            ) : null}
          </div>

          <Divider sx={{ my: '20px' }} />

          {selectedSurvey.isLive || selectedSurvey.isCompleted ? renderSurveyStats() : null}

          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={12} md={12} lg={7} xl={7}>
              <SurveyCard />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={5} xl={5}>
              <PeopleCard />
            </Grid>
          </Grid>

          {isEditing ? (
            <SurveySchedule open={isEditing} requestClose={() => setIsEditing(false)} />
          ) : null}
        </>
      )}
    </>
  )
})

const LOADER: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}
